import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, ButtonGroup, Form, Modal } from 'react-bootstrap';
import axios from 'axios';
import { CheckCircle2, FileText } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import RichTextEditor from './RichTextEditor';
import SignatureField from './SignatureField';
import { formHasAllRequiredSignatures, normalizeFormFields } from '../utils/signatureFieldUtils';

const normalizeFields = normalizeFormFields;

const getTableCellKey = (fieldKey, rowIndex, colIndex) => `${fieldKey}__cell_${rowIndex}_${colIndex}`;
const formatPhoneValue = (input) => {
  const digits = String(input || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const formatSsnValue = (input) => {
  const digits = String(input || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};
const maskSsnValue = (input) => {
  const formatted = formatSsnValue(input);
  if (!formatted) return '';
  const digits = formatted.replace(/\D/g, '');
  const last4 = digits.slice(-4);
  return last4 ? `***-**-${last4}` : '***-**-****';
};

const getContainerRows = (container) => {
  if (Array.isArray(container?.containerRows) && container.containerRows.length > 0) return container.containerRows;
  if (Array.isArray(container?.nestedFields) && container.nestedFields.length > 0) return [{ id: `row_${container.id || 'legacy'}`, fields: container.nestedFields }];
  return [];
};

const collectInputFields = (fields) => {
  const out = [];
  const walk = (list) => {
    (list || []).forEach((f) => {
      if (!f) return;
      if (f.isContainer) {
        getContainerRows(f).forEach((row) => walk(row.fields || []));
        return;
      }
      if (['header', 'textinfo', 'divider', 'spacer', 'header_logo_content'].includes(f.type)) return;
      out.push(f);
    });
  };
  walk(fields);
  return out;
};

const coerceInitialValues = (fields, initialData) => {
  const out = {};
  fields.forEach((f) => {
    const key = f?.key || f?.id || '';
    if (!key) return;
    if (f.type === 'table' && Array.isArray(f.tableData)) {
      f.tableData.forEach((row, r) => {
        (row || []).forEach((cell, c) => {
          const isHeaderOrFirstCol = r === 0 || c === 0;
          const hasTemplateText = String(cell?.content || '').trim().length > 0;
          // Header row / first column stay static only when template text exists.
          // Otherwise, expose them as user-editable inputs like normal cells.
          if (isHeaderOrFirstCol && hasTemplateText) return;
          const cellKey = getTableCellKey(key, r, c);
          const sourceValue = initialData?.[cellKey];
          out[cellKey] = sourceValue !== undefined ? sourceValue : String(cell?.content || '');
        });
      });
      return;
    }
    const sourceValue = initialData?.[key];
    if (sourceValue !== undefined) {
      out[key] = sourceValue;
      return;
    }
    if (f.type === 'checkbox') {
      out[key] = false;
      return;
    }
    if (f.type === 'yes_no' || f.type === 'true_false') {
      const sourceBool = initialData?.[key];
      if (typeof sourceBool === 'boolean') out[key] = sourceBool;
      else if (sourceBool === 'true') out[key] = true;
      else if (sourceBool === 'false') out[key] = false;
      else out[key] = null;
      return;
    }
    if (f.type === 'signature') {
      if (initialData?.[key] !== undefined) out[key] = initialData[key];
      else out[key] = { status: 'unsigned', draftConsents: {}, draftSignerName: '' };
      return;
    }
    out[key] = '';
  });
  return out;
};

const renderFieldInput = (field, value, onChange, readOnly = false) => {
  const key = field?.key || field?.id || '';
  const type = field?.type || 'text';
  const options = Array.isArray(field?.options) ? field.options : [];

  if (type === 'textarea') {
    return (
      <Form.Control
        as="textarea"
        rows={3}
        value={value || ''}
        disabled={readOnly}
        onChange={(e) => onChange(key, e.target.value)}
      />
    );
  }
  if (type === 'rich_textarea') {
    if (readOnly) {
      return (
        <div
          className="border rounded p-2 bg-light-subtle"
          dangerouslySetInnerHTML={{ __html: value || '<p class="text-muted mb-0">No response provided.</p>' }}
        />
      );
    }
    return (
      <RichTextEditor
        value={value || ''}
        onChange={(nextValue) => onChange(key, nextValue)}
        placeholder={field?.placeholder || 'Enter response...'}
      />
    );
  }
  if (type === 'select' || type === 'dropdown') {
    return (
      <Form.Select value={value || ''} disabled={readOnly} onChange={(e) => onChange(key, e.target.value)}>
        <option value="">Select an option</option>
        {options.map((option) => {
          const optionValue = typeof option === 'string' ? option : option?.value || option?.label;
          const optionLabel = typeof option === 'string' ? option : option?.label || option?.value;
          return (
            <option key={`${key}-${optionValue}`} value={optionValue || ''}>
              {optionLabel || optionValue}
            </option>
          );
        })}
      </Form.Select>
    );
  }
  if (type === 'checkbox') {
    return (
      <Form.Check
        type="checkbox"
        checked={Boolean(value)}
        disabled={readOnly}
        onChange={(e) => onChange(key, e.target.checked)}
      />
    );
  }
  if (type === 'yes_no' || type === 'true_false') {
    const positiveLabel = type === 'yes_no' ? 'Yes' : 'True';
    const negativeLabel = type === 'yes_no' ? 'No' : 'False';
    return (
      <ButtonGroup size="sm">
        <Button type="button" variant={value === true ? 'primary' : 'outline-secondary'} disabled={readOnly} onClick={() => onChange(key, true)}>
          {positiveLabel}
        </Button>
        <Button type="button" variant={value === false ? 'primary' : 'outline-secondary'} disabled={readOnly} onClick={() => onChange(key, false)}>
          {negativeLabel}
        </Button>
      </ButtonGroup>
    );
  }
  if (type === 'phone') {
    return (
      <Form.Control
        type="tel"
        value={formatPhoneValue(value)}
        disabled={readOnly}
        onChange={(e) => onChange(key, formatPhoneValue(e.target.value))}
        placeholder="(###) ###-####"
      />
    );
  }
  if (type === 'ssn') {
    return (
      <Form.Control
        type={readOnly ? 'text' : 'password'}
        value={readOnly ? maskSsnValue(value) : formatSsnValue(value)}
        disabled={readOnly}
        onChange={(e) => onChange(key, formatSsnValue(e.target.value))}
        placeholder="###-##-####"
      />
    );
  }
  return (
    <Form.Control
      type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
      value={value || ''}
      disabled={readOnly}
      onChange={(e) => onChange(key, e.target.value)}
    />
  );
};

const OnboardingFormModal = ({
  show,
  onHide,
  form,
  assignment,
  initialData,
  onSubmit,
  readOnly = false,
  reviewNotes = '',
  signContext = null
}) => {
  const [values, setValues] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signingFieldId, setSigningFieldId] = useState(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const fields = useMemo(() => normalizeFields(form?.schema), [form]);
  const inputFields = useMemo(() => collectInputFields(fields), [fields]);

  useEffect(() => {
    if (!show) return;
    setError('');
    setValues(coerceInitialValues(inputFields, initialData));
  }, [show, inputFields, initialData]);

  const handleChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSignField = async (field, payload) => {
    const fieldId = field?.id || field?.key;
    if (!fieldId || !signContext) {
      throw new Error('Signing is not available for this form.');
    }
    setSigningFieldId(fieldId);
    try {
      let res;
      if (signContext.mode === 'package') {
        res = await axios.post(
          `${API_ENDPOINTS.ONBOARDING}/package-section-assignments/${signContext.sectionAssignmentId}/items/${signContext.itemId}/sign-field`,
          { fieldId, signerName: payload.signerName, consents: payload.consents },
          { headers }
        );
      } else {
        res = await axios.post(
          `${API_ENDPOINTS.ONBOARDING}/assignments/${signContext.assignmentId}/sign-field`,
          { fieldId, signerName: payload.signerName, consents: payload.consents },
          { headers }
        );
      }
      const nextFormData = res.data?.formData;
      if (nextFormData && typeof nextFormData === 'object') {
        setValues((prev) => coerceInitialValues(inputFields, { ...prev, ...nextFormData }));
      } else if (res.data?.value) {
        setValues((prev) => ({ ...prev, [fieldId]: res.data.value }));
      }
    } finally {
      setSigningFieldId(null);
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (!formHasAllRequiredSignatures(fields, values)) {
        setError('Complete and sign all required signature fields before submitting.');
        return;
      }
      setSubmitting(true);
      await onSubmit?.(values);
      onHide?.();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const getContainerStyle = (field) => {
    const rawBg = String(field.backgroundColor || '').trim().toLowerCase();
    const hasBackgroundColor = Boolean(rawBg && rawBg !== 'transparent' && rawBg !== 'none');
    const style = {
      backgroundColor: hasBackgroundColor ? field.backgroundColor : 'transparent'
    };
    const borderColor = field.borderColor || 'var(--gray-400)';
    const sides = Array.isArray(field.borderSides) ? field.borderSides : [];
    if (sides.includes('all')) {
      style.border = `2px solid ${borderColor}`;
    } else {
      if (sides.includes('top')) style.borderTop = `2px solid ${borderColor}`;
      if (sides.includes('right')) style.borderRight = `2px solid ${borderColor}`;
      if (sides.includes('bottom')) style.borderBottom = `2px solid ${borderColor}`;
      if (sides.includes('left')) style.borderLeft = `2px solid ${borderColor}`;
    }
    return style;
  };

  const getContainerEntryKey = (container) => `__container_entries__${container?.id || container?.key || 'container'}`;
  const resolveFieldKeyForEntry = (baseFieldKey, entryId) => (entryId === 'legacy' ? baseFieldKey : `${baseFieldKey}__entry_${entryId}`);
  const getContainerEntryIds = (container) => {
    if (!container?.allowMultipleEntries) return ['legacy'];
    const raw = values[getContainerEntryKey(container)];
    if (Array.isArray(raw) && raw.length > 0) return raw;
    return ['legacy'];
  };
  const evaluateConditionalRule = (rule, entryId = 'legacy') => {
    if (!rule?.sourceFieldId) return true;
    const sourceKey = entryId === 'legacy' ? rule.sourceFieldId : `${rule.sourceFieldId}__entry_${entryId}`;
    return values?.[sourceKey] === true;
  };
  const addContainerEntry = (container) => {
    if (readOnly || !container?.allowMultipleEntries) return;
    const nextId = `entry_${Date.now()}_${Math.round(Math.random() * 1e4)}`;
    const key = getContainerEntryKey(container);
    setValues((prev) => {
      const currentEntries = Array.isArray(prev[key]) && prev[key].length > 0 ? prev[key] : ['legacy'];
      return { ...prev, [key]: [...currentEntries, nextId] };
    });
  };
  const removeContainerEntry = (container, entryId) => {
    if (readOnly || !container?.allowMultipleEntries || entryId === 'legacy') return;
    const key = getContainerEntryKey(container);
    const entryToken = `__entry_${entryId}`;
    setValues((prev) => {
      const next = { ...prev };
      const currentEntries = Array.isArray(next[key]) && next[key].length > 0 ? next[key] : ['legacy'];
      next[key] = currentEntries.filter((id) => id !== entryId);
      Object.keys(next).forEach((k) => {
        if (k.includes(entryToken)) delete next[k];
      });
      return next;
    });
  };

  const renderPreviewField = (field, parentContainer = null, keyPrefix = 'field', entryId = 'legacy') => {
    const baseKey = field?.key || field?.id || `${keyPrefix}_key`;
    const key = resolveFieldKeyForEntry(baseKey, entryId);
    const type = field?.type || 'text';

    if (type === 'header') {
      const Tag = field.headingLevel || 'h3';
      const alignment = field.headerAlignment || 'left';
      const alignmentClass = alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-end' : 'text-start';
      return (
        <div className={alignmentClass}>
          <Tag className="mb-1">{field.label}</Tag>
          {field.headerSubtitle ? <div className="text-muted fw-semibold mb-1">{field.headerSubtitle}</div> : null}
          {field.headerDescription ? <p className="text-muted small mb-2">{field.headerDescription}</p> : null}
        </div>
      );
    }
    if (type === 'header_logo_content') {
      const container = parentContainer || {};
      return (
        <div className={`d-flex flex-row gap-2 align-items-center ${container.alignment === 'center' ? 'justify-content-center' : container.alignment === 'right' ? 'justify-content-end' : 'justify-content-start'}`}>
          {container.logo ? <img src={container.logo} alt="Form logo" style={{ maxHeight: 48, objectFit: 'contain' }} /> : null}
          <div className="d-flex flex-column">
            {container.headerTitle ? <h5 className="mb-0 fw-semibold">{container.headerTitle}</h5> : null}
            {container.headerSubtitle ? <p className="text-muted mb-0">{container.headerSubtitle}</p> : null}
          </div>
        </div>
      );
    }
    if (type === 'textinfo') return <div className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: field.textContent || '' }} />;
    if (type === 'divider') {
      const spacing = Number(field.dividerSpacing ?? 16);
      const dividerStyle = field.dividerStyle || 'solid';
      const dividerThickness = Number(field.dividerThickness || 1);
      const dividerColor = field.dividerColor || 'var(--gray-400)';
      return (
        <div style={{ paddingTop: spacing, paddingBottom: spacing }}>
          <hr className="m-0" style={{ borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}` }} />
        </div>
      );
    }
    if (type === 'spacer') return <div style={{ height: Number(field.spacerHeight || 24) }} />;
    if (type === 'table' && Array.isArray(field.tableData)) {
      return (
        <div className="d-flex flex-column gap-1">
          {field.tableTitle ? <div className="fw-semibold">{field.tableTitle}</div> : null}
          {field.tableSubtitle ? <div className="text-muted fw-semibold">{field.tableSubtitle}</div> : null}
          {field.tableDescription ? <p className="text-muted small mb-1">{field.tableDescription}</p> : null}
          <div className="table-responsive">
            <table className="table align-middle mb-0 custom-border rounded-3 border-1 overflow-hidden">
              <tbody>
                {field.tableData.map((row, r) => (
                  <tr key={`${key}_row_${r}`}>
                    {row.map((cell, c) => {
                      const isHeaderOrFirstCol = r === 0 || c === 0;
                      const isTopHeaderCell = r === 0;
                      const hasTemplateText = String(cell?.content || '').trim().length > 0;
                      const CellTag = r === 0 ? 'th' : 'td';
                      const cellKey = getTableCellKey(key, r, c);
                      const cellStyle = {
                        ...(isTopHeaderCell ? { backgroundColor: 'var(--custom-header-bg, #f3f4f6)' } : {}),
                      };
                      return (
                        <CellTag key={cell.id || `${key}_cell_${r}_${c}`} style={cellStyle}>
                          {isHeaderOrFirstCol && hasTemplateText ? (
                            <div>{cell.content}</div>
                          ) : (
                            <Form.Control
                              value={values[cellKey] || ''}
                              onChange={(e) => handleChange(cellKey, e.target.value)}
                              placeholder={r === 0 ? 'Header' : c === 0 ? 'Row label' : 'Value'}
                            />
                          )}
                        </CellTag>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (type === 'dropdown' || type === 'select' || type === 'checkbox') {
      const displayChoiceTitle = field?.choiceTitle || field?.label || key;
      return (
        <Form.Group>
          {(displayChoiceTitle || field?.required) ? (
            <Form.Label className="fw-semibold">
              {displayChoiceTitle || ''} {field?.required ? <span className="text-danger">*</span> : null}
            </Form.Label>
          ) : null}
          {field?.choiceDescription ? (
            <div className="text-muted small mb-2">{field.choiceDescription}</div>
          ) : null}
          {renderFieldInput(field, values[key], (_, nextVal) => handleChange(key, nextVal), readOnly)}
        </Form.Group>
      );
    }
    if (type === 'yes_no' || type === 'true_false') {
      const isVerticalLayout = (field?.booleanLayout ?? 'horizontal') === 'vertical';
      return (
        <Form.Group className={isVerticalLayout ? 'd-flex flex-column align-items-start gap-2 mb-2' : 'd-flex align-items-center justify-content-between gap-2 mb-2'}>
          <Form.Label className="fw-semibold mb-0">
            {field?.label || key} {field?.required ? <span className="text-danger">*</span> : null}
          </Form.Label>
          {renderFieldInput(field, values[key], (_, nextVal) => handleChange(key, nextVal), readOnly)}
        </Form.Group>
      );
    }
    if (type === 'signature') {
      const fieldId = field?.id || key;
      return (
        <Form.Group>
          <Form.Label className="fw-semibold">
            {field?.label || 'Signature'} {field?.required ? <span className="text-danger">*</span> : null}
          </Form.Label>
          <SignatureField
            field={field}
            value={values[key]}
            readOnly={readOnly}
            signing={signingFieldId === fieldId}
            onDraftChange={(draft) => handleChange(key, draft)}
            onSign={(payload) => handleSignField(field, payload)}
          />
        </Form.Group>
      );
    }

    return (
      <Form.Group>
        {!['header', 'textinfo', 'header_logo_content', 'divider', 'spacer', 'table', 'dropdown', 'select', 'checkbox', 'signature'].includes(type) && (
          <Form.Label className="fw-semibold">
            {field?.label || key} {field?.required ? <span className="text-danger">*</span> : null}
          </Form.Label>
        )}
        {renderFieldInput(field, values[key], (_, nextVal) => handleChange(key, nextVal), readOnly)}
      </Form.Group>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className=' px-4'>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FileText />
          <span>{form?.title || assignment?.customName || 'Form'}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {reviewNotes ? <Alert variant="warning" className="py-2"><strong>Reviewer notes:</strong> {reviewNotes}</Alert> : null}
        {/* {form?.description && <div className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: form.description }} />} */}
        {!fields.length ? (
          <Alert variant="warning" className="mb-0">This form does not have any fields yet.</Alert>
        ) : (
          <Form className="d-flex flex-column gap-3">
            {fields.map((field, idx) => (
              <div key={field?.id || `field_${idx}`}>
                {evaluateConditionalRule(field.conditionalRule) ? (!field?.isContainer ? (
                  <div>{renderPreviewField(field, null, `top_${idx}`)}</div>
                ) : (
                  <div className={`rounded position-relative ${((field.backgroundColor && !['transparent', 'none'].includes(String(field.backgroundColor).toLowerCase())) || (Array.isArray(field.borderSides) && field.borderSides.length > 0)) ? 'p-3' : ''}`} style={getContainerStyle(field)}>
                    {(field.sectionTitle || field.sectionSubtitle || field.sectionDescription) && (
                      <>
                        {field.sectionTitle && <h6 className="mb-1 fw-semibold">{field.sectionTitle}</h6>}
                        {field.sectionSubtitle && <div className="text-muted fw-semibold mb-1">{field.sectionSubtitle}</div>}
                        {field.sectionDescription && <p className="text-muted small mb-2">{field.sectionDescription}</p>}
                      </>
                    )}
                    <div className="d-flex flex-column gap-2">
                      {getContainerEntryIds(field).map((entryId, entryIdx) => (
                        <div key={`${field.id || `container_${idx}`}_entry_${entryId}`} className={entryIdx > 0 ? 'pt-2 mt-1 border-top' : ''}>
                          {field.allowMultipleEntries && (
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                              <div className="text-muted small">Entry {entryIdx + 1}</div>
                              {!readOnly && entryId !== 'legacy' && (
                                <Button size="sm" variant="outline-danger" onClick={() => removeContainerEntry(field, entryId)}>
                                  Remove entry
                                </Button>
                              )}
                            </div>
                          )}
                          {getContainerRows(field).map((row, rowIdx) => {
                            if (!evaluateConditionalRule(row.conditionalRule, entryId)) return null;
                            const rowHasBooleanChoice = (row.fields || []).some((nf) => ['yes_no', 'true_false'].includes(nf?.type));
                            return (
                        <div key={`${row.id || `row_${rowIdx}`}_${entryId}`}>
                          <div className={`d-flex gap-2 ${rowHasBooleanChoice ? 'align-items-end' : ''}`}>
                            {(row.fields || []).map((nf, nfIdx) => (
                              !evaluateConditionalRule(nf.conditionalRule, entryId) ? null : (
                              <div key={`${nf.id || `nf_${nfIdx}`}_${entryId}`} style={{ flex: nf.flexBasis || 1 }} className="position-relative">
                                {renderPreviewField(nf, field, `nested_${idx}_${rowIdx}_${nfIdx}`, entryId)}
                              </div>
                              )
                            ))}
                          </div>
                        </div>
                      );
                          })}
                        </div>
                      ))}
                      {!readOnly && field.allowMultipleEntries && (
                        <Button size="sm" variant="outline-primary" className="align-self-start mt-1" onClick={() => addContainerEntry(field)}>
                          Add another entry
                        </Button>
                      )}
                    </div>
                  </div>
                )) : null}
              </div>
            ))}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={submitting}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button variant="primary" size="sm" className='d-flex align-items-center gap-2' onClick={handleSubmit} disabled={submitting || !inputFields.length}>
            <CheckCircle2  />
            {submitting ? 'Submitting...' : 'Submit Form'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default OnboardingFormModal;

